import { Box, Button, Stack, styled, Typography, useMediaQuery, useTheme } from "@mui/material";
import { HorizontalGaugeChart, FoodsIcon, FoodsTags } from "@foodpilot/foods";
import i18n from "src/i18n";
import { t } from "i18next";
import { formatNumber } from "src/utils/formatting";
import { EmptyCard } from "./EmptyCard";
import { ReactNode } from "react";
import { useEntityScores } from "src/context/Scores/useEntityScores";

export type ComparisonData = {
    key: string;
    title: string;

    min: number | undefined | null;
    max: number | undefined | null;
    value: number | undefined | null;
    average: number | undefined | null;
    median: number | undefined | null;
    count: number | undefined | null;

    action?: () => void;

    noData?: boolean;
    noDimensions?: boolean;
};

type ScoreCardProps = {
    score: ComparisonData | undefined;
    unit: string;
    selectorComponent?: ReactNode;
};

const MINIMUM_COMPARISON_SAMPLE_SIZE: number = 10;

export const ComparisonCard = (props: ScoreCardProps) => {
    const { score, unit, selectorComponent } = props;
    const theme = useTheme();
    const { lastSelectedVariation } = useEntityScores();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    if (!score) return null;

    const isCardEmpty = score.noData || score === undefined;
    const medianValue = score.median;
    const isSampleSufficient = !isCardEmpty && !!score.count && score.count >= MINIMUM_COMPARISON_SAMPLE_SIZE;
    const canRenderComparison = isSampleSufficient && typeof medianValue === "number";
    return (
        <ComparisonCardOuter>
            {selectorComponent && <Box sx={(theme) => ({ marginBottom: theme.spacing(3) })}>{selectorComponent}</Box>}
            <ComparisonCardContent>
                {!(isMobile && selectorComponent) && (
                    <ComparisonHeader>
                        <Typography variant="h4">
                            {score.title}
                            <Typography variant="body" color="grey.900">
                                {unit ? ` - ${unit}` : ""}
                            </Typography>
                        </Typography>
                    </ComparisonHeader>
                )}
                {!canRenderComparison ?
                    <EmptyCard direction={isMobile ? "column" : "row"} />
                :   <Stack gap={theme.spacing(3)} alignItems="flex-start">
                        <HorizontalGaugeChart
                            minValue={score.min ?? 0}
                            maxValue={score.max ?? 9999}
                            medianValue={medianValue}
                            companyValue={score.value ?? 0}
                            averageValue={score.average ?? 0}
                            unit={unit}
                            labelCompany={t("score.details.comparison.company")}
                            labelAverage={t("score.details.comparison.average")}
                            labelMinimum={t("score.details.comparison.minimum", "Minimum")}
                            labelMedian={t("score.details.comparison.median", "Median")}
                            labelMaximum={t("score.details.comparison.maximum", "Maximum")}
                            ariaLabel={t("score.details.comparison.chart_label", "Comparison with group statistics")}
                            backgroundProps={{
                                gradient: {
                                    start: theme.palette.primary[100]!,
                                    end: theme.palette.primary[900]!,
                                },
                            }}
                            valueFormatter={(value) =>
                                formatNumber(value, i18n.resolvedLanguage, {
                                    minimumFractionDigits: lastSelectedVariation.precision,
                                    maximumFractionDigits: lastSelectedVariation.precision,
                                })
                            }
                        />
                        <SampleSizeTag count={score.count ?? 0} />
                    </Stack>
                }
            </ComparisonCardContent>

            {score.action !== undefined && canRenderComparison && (
                <ComparisonDetailButton
                    onClick={() => {
                        score.action?.();
                    }}
                >
                    {t("score.details.comparison.seedetails")}
                    <FoodsIcon icon="arrowRight" size={1} />
                </ComparisonDetailButton>
            )}
        </ComparisonCardOuter>
    );
};

type SampleSizeTagProps = {
    count: number;
};

const SampleSizeTag = (props: SampleSizeTagProps) => {
    const { count } = props;

    return (
        <FoodsTags
            label={t("score.details.comparison.based_on", {
                defaultValue: "Comparison based on {{ size }} companies",
                size: count,
            })}
            variant="tags-white"
            disabled
        />
    );
};

const ComparisonCardOuter = styled(Box)(() => ({
    height: "100%",
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "column",
}));

const ComparisonCardContent = styled(Box)(() => ({
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
}));

const ComparisonDetailButton = styled(Button)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 0,
    padding: theme.spacing(2, 0),
    width: "100%",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3),
    color: theme.palette.primary[700],
    "&:hover": {
        textDecoration: "none",
        color: theme.palette.primary[900],
    },
}));

const ComparisonHeader = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(4),
    display: "inline-flex",
    gap: theme.spacing(0.5),
}));
